// ============================================================
// account/AccountScreens.js — the sign-up and sign-in screens, pricing, and the screen after paying on Stripe.
//
// In this file, in order:
//   Sign-up and sign-in screens
//   Back from Stripe
//   Pricing
// ============================================================

import React, { useState } from "react";
import { JURISDICTION_TYPES, LEGAL, PLANS, STATES, STRIPE, daysLeft, departmentsFor, finishCheckout, formatDate, paymentsConfigured, planById, priceFor, purchase, readCheckoutReturn, useAuth } from "./AccountContext";
import { CheckIcon, Control, CopyButton, DownloadButton, ErrorText, Field, Pill, SectionHeading, Select, StatusRow, Tag } from "../components/ui";
import { useNavigation } from "../context/Navigation";
import { downloadBlob, mailto } from "../lib/browser";
import { useOnce } from "../lib/hooks";

/* ============================================================
   Sign-up and sign-in screens
   ============================================================ */

export function passcodeFile(name, code, details) {
  const lines = [`${name} · Gov Social Kit passcode`, "", code, "",
    `Sign in with your workspace name ("${name}") and this passcode.`, "It can't be reset, so keep this file somewhere safe."];
  if (details) {
    lines.push("", `Plan: ${planById(details.plan).name}`, `Valid until: ${formatDate(details.expiresAt)}`);
    if (details.checkoutId) lines.push(`Stripe checkout: ${details.checkoutId}`);
  }
  return lines.join("\n") + "\n";
}

/* ------------------------------------------------------------ passcode card */

export function PasscodeCard({ name, code, masked = false, details }) {
  const [revealed, setRevealed] = useState(!masked);

  return (
    <div className="passcode">
      <code className={"passcode__code" + (revealed ? "" : " passcode__code--masked")} aria-label="Passcode">
        {revealed ? code : code.replace(/[^-]/g, "•")}
      </code>
      <div className="aside-panel__actions">
        {masked && <Pill outline onClick={() => setRevealed((r) => !r)}>{revealed ? "Hide" : "Show"}</Pill>}
        <CopyButton text={code} onFail={() => setRevealed(true)} />
        <DownloadButton onClick={() => downloadBlob(passcodeFile(name, code, details), "passcode.txt")}>Download</DownloadButton>
        {/* Opens your own mail app with the passcode in a message to yourself: nothing is sent from here. */}
        <a className="btn-pill btn-pill--outline"
           href={mailto("", `${name} · Gov Social Kit passcode`, passcodeFile(name, code, details))}>
          Email it to me
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ sign-in gate */

/** The centred card the sign-in, passcode and checkout screens sit on (in a page, or embedded). */
function GateCard({ embedded, children }) {
  const Wrapper = embedded ? "div" : "main";
  return (
    <Wrapper className={"gate" + (embedded ? " gate--embedded" : "")}>
      <div className="gate__card u-fade-in">
        {!embedded && <p className="gate__brand">Gov Social Kit</p>}
        {children}
      </div>
    </Wrapper>
  );
}

function ErrorRow({ message }) {
  if (!message) return null;
  return (
    <StatusRow tone="error" role="alert">{message}</StatusRow>
  );
}

// Plans to pick from on the "Get started" form.
function PlanPicker({ value, onChange }) {
  return (
    <fieldset className="plan-pick aside-panel__field">
      <legend className="aside-panel__label">Plan</legend>
      {PLANS.map((p) => (
        <label key={p.id} className={"plan-pick__option" + (value === p.id ? " plan-pick__option--on" : "")}>
          <input type="radio" name="plan" value={p.id} checked={value === p.id} onChange={() => onChange(p.id)} />
          <span><strong>{p.name}</strong> <span className="plan-pick__price">{p.price} {p.period}</span></span>
        </label>
      ))}
    </fieldset>
  );
}

// Typing a passcode: letters and numbers only, grouped XXXX-XXXX-XXXX as you type.
const tidyCode = (v) => v.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 12).replace(/(.{4})(?=.)/g, "$1-");

export function AccountGate({ onPasscode, initialMode = "create", initialPlan = "department", embedded = false, notice }) {
  const { login, applyPurchase } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [plan, setPlan] = useState(initialPlan);
  const [name, setName] = useState("");
  const [type, setType] = useState("county");
  const [state, setState] = useState("Iowa");
  const [department, setDepartment] = useState("general");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(null); // { code, name, plan } of an ended passcode
  const testMode = !paymentsConfigured();

  async function buy(pending) {
    setBusy(true);
    try {
      const done = await purchase(pending, { applyPurchase, email });
      if (done) onPasscode(done.code, done.details); // test mode; otherwise we're on our way to Stripe
    } catch (err) {
      setError(err.message || "That didn't work. Check your details and try again.");
      setBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    setExpired(null);
    if (!name.trim()) { setError("Enter your workspace name."); return; }
    if (mode === "create") return buy({ kind: "new", plan, name: name.trim(), jurisdiction: type, state, department });
    setBusy(true);
    try {
      await login({ name, code, keep });
    } catch (err) {
      setError(err.message || "That didn't work. Check your details and try again.");
      if (err.expired) setExpired(err.expired);
      setBusy(false);
    }
  }

  const chosen = planById(plan);
  return (
    <GateCard embedded={embedded}>
        {notice && (
          <StatusRow tone="working">{notice}</StatusRow>
        )}
        <SectionHeading
          title={mode === "create" ? "Get started" : "Log in"}
          subtitle={mode === "create"
            ? "Set up your workspace, then pay securely on Stripe. You'll get your passcode as soon as you're back."
            : "Use your workspace name and the passcode you saved."}
        />
        <div className="aside-panel__field">
          <div className="aside-panel__swatches">
            <Pill outline active={mode === "create"} onClick={() => { setMode("create"); setError(""); }}>New workspace</Pill>
            <Pill outline active={mode === "login"} onClick={() => { setMode("login"); setError(""); }}>Log in</Pill>
          </div>
        </div>

        <form onSubmit={submit} noValidate>
          {mode === "create" && <PlanPicker value={plan} onChange={setPlan} />}
          <Field id="gate-name" label="Workspace name" hint={mode === "create" && "Your passcode only works with this exact name, so use the one your team will type."}>
            <input id="gate-name" className="size-form__input layer-editor__input" value={name} autoComplete="organization"
                   onChange={(e) => { setName(e.target.value); setExpired(null); }} placeholder="e.g. Adams County" />
          </Field>

          {mode === "create" ? (
            <>
              <div className="layer-editor__grid aside-panel__field">
                <Control label="Jurisdiction">
                  <Select id="gate-type" value={type} options={JURISDICTION_TYPES.map((t) => [t.id, t.label])}
                          onChange={(v) => { setType(v); setDepartment(departmentsFor(v)[0].id); }} />
                </Control>
                <Control label="State">
                  <Select value={state} onChange={setState} options={STATES.map((s) => [s, s])} />
                </Control>
                <Control label={plan === "all" ? "Start in department" : "Department"} className="layer-editor__control gate__wide">
                  <Select id="gate-department" value={department} onChange={setDepartment} options={departmentsFor(type).map((d) => [d.id, d.label])} />
                </Control>
              </div>
              {!testMode && (
                <Field id="gate-email" label="Email for your receipt (optional)" hint="Filled in for you on Stripe. It isn't stored here.">
                  <input id="gate-email" type="email" className="size-form__input layer-editor__input" value={email} autoComplete="email"
                         onChange={(e) => setEmail(e.target.value)} placeholder="you@county.gov" />
                </Field>
              )}
            </>
          ) : (
            <>
              <Field id="gate-code" label="Passcode" hint="Your plan, state, jurisdiction and department come from the passcode.">
                <span className="passcode__field">
                  <input id="gate-code" className="size-form__input layer-editor__input passcode__input" type={showCode ? "text" : "password"}
                         value={code} onChange={(e) => { setCode(tidyCode(e.target.value)); setExpired(null); }} spellCheck={false} autoCapitalize="characters"
                         autoComplete="current-password" placeholder="XXXX-XXXX-XXXX" inputMode="text" maxLength={14} />
                  <Pill outline onClick={() => setShowCode((v) => !v)} aria-pressed={showCode}>{showCode ? "Hide" : "Show"}</Pill>
                </span>
              </Field>
              <label className="gate__confirm">
                <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
                <span>Keep me signed in on this device <span className="contrast-note">Turn off on a shared computer: you're signed out when the browser closes.</span></span>
              </label>
            </>
          )}

          <ErrorRow message={error} />
          {expired && (
            <button type="button" className="aside-panel__save u-btn-solid gate__submit" disabled={busy}
                    onClick={() => buy({ kind: "renew", plan: expired.plan, name: expired.name, code: expired.code })}>
              {expired.trial
                ? (testMode ? "Choose a plan (test mode)" : `Keep this workspace: a year for ${priceFor({ kind: "renew", plan: expired.plan })}`)
                : testMode ? "Renew for a year (test mode)" : `Renew for a year · ${priceFor({ kind: "renew", plan: expired.plan })}`}
            </button>
          )}
          {!expired && (
            <button type="submit" className="aside-panel__save u-btn-solid gate__submit" disabled={busy}>
              {busy ? "One moment…"
                : mode === "login" ? "Log in"
                : testMode ? "Create workspace (test mode, no payment)"
                : `Continue to payment · ${chosen.price} ${chosen.period}`}
            </button>
          )}
          {mode === "create" && (
            <p className="contrast-note gate__fine">
              {testMode
                ? "Test mode: Stripe Payment Links aren't set up in src/account/AccountContext.js yet, so no payment is taken."
                : <React.Fragment>You'll pay on Stripe's secure page; this site never sees your card. Need an invoice or purchase order? Email {LEGAL.contactEmail ? <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> : "us"} first.</React.Fragment>}
            </p>
          )}
          {mode === "login" && (
            <details className="gate__help">
              <summary>Lost your passcode?</summary>
              <p className="contrast-note">
                Passcodes aren't stored anywhere but with you, so they can't be reset. Look for the passcode.txt file you
                downloaded, the email you sent yourself, or ask a colleague signed in to the same workspace (Settings shows it).
                {LEGAL.contactEmail && <React.Fragment> Still stuck? Email <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> with your Stripe receipt.</React.Fragment>}
              </p>
            </details>
          )}
        </form>
    </GateCard>
  );
}

export function PasscodeReveal({ code, name, onDone, details, title = "Save your passcode", intro }) {
  const [saved, setSaved] = useState(false);
  return (
    <GateCard>
        <SectionHeading
          title={title}
          subtitle={intro || "This passcode is how you sign in, on this device or any other. There's no reset, so download it or store it in your password manager now."}
        />
        {details && (
          <StatusRow tone="done">
            {details.kind === "renew" ? "Renewed." : details.kind === "upgrade" ? "Upgraded to all departments." : "You're all set."}
            {details.plan ? ` ${planById(details.plan).name}` : ""}
            {details.expiresAt ? `, valid until ${formatDate(details.expiresAt)}` : ""}.
            {details.kind && details.kind !== "new" ? " Your old passcode stops working at its end date: use this one from now on." : ""}
            {details.test ? " (Test mode: no payment was taken.)" : ""}
          </StatusRow>
        )}
        {code ? <PasscodeCard name={name} code={code} details={details} /> : <p className="contrast-note">Creating your passcode…</p>}
        {details && details.checkoutId && <p className="contrast-note">Stripe checkout ID, for your records: <code>{details.checkoutId}</code></p>}
        <label className="gate__confirm">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} disabled={!code} />
          <span>I've saved my passcode somewhere safe</span>
        </label>
        <button type="button" className="aside-panel__save u-btn-solid gate__submit" disabled={!saved} onClick={onDone}>
          Continue
        </button>
    </GateCard>
  );
}

/* ------------------------------------------------------------ plan (shown in Settings) */

export function PlanSection({ session, onPasscode }) {
  const { applyPurchase } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const plan = planById(session.plan);
  const left = daysLeft(session.expiresAt);
  const testMode = !paymentsConfigured();

  async function buy(kind) {
    setBusy(true); setError("");
    try {
      const done = await purchase({ kind, plan: session.plan, name: session.name, code: session.code }, { applyPurchase });
      if (done) { onPasscode(done.code, done.details); setBusy(false); }
    } catch (err) {
      setError(err.message || "That didn't work."); setBusy(false);
    }
  }

  return (
    <div className="aside-panel__field">
      <div className="aside-panel__rows aside-panel__rows--flush">
        <div className="aside-panel__row">
          <span>
            {session.trial ? `Trial · ${plan.name}` : plan.name} <span className="contrast-note">{session.trial ? `then ${plan.price} ${plan.period}` : `${plan.price} ${plan.period}`}</span>
          </span>
          <Tag tone={left > 30 ? "pass" : "warning"}>{left > 30 ? "Active" : left <= 0 ? "Ends today" : `${left} day${left === 1 ? "" : "s"} left`}</Tag>
        </div>
        <div className="aside-panel__row"><span>Valid until</span><strong>{formatDate(session.expiresAt)}</strong></div>
      </div>
      <div className="aside-panel__actions">
        <Pill outline={left > 60} onClick={() => buy("renew")} disabled={busy}>
          {session.trial ? "Choose this plan for a year" : "Renew for a year"}{testMode ? " (test mode)" : ` · ${plan.price}`}
        </Pill>
        {session.plan !== "all" && (
          <Pill outline onClick={() => buy("upgrade")} disabled={busy}>
            Upgrade to all departments{testMode ? " (test mode)" : ` · ${priceFor({ kind: "upgrade" })}`}
          </Pill>
        )}
        {STRIPE.customerPortalUrl && <a className="btn-pill btn-pill--outline" href={STRIPE.customerPortalUrl} target="_blank" rel="noreferrer">Receipts and invoices</a>}
      </div>
      <span className="contrast-note">
        {testMode
          ? "Test mode: Stripe Payment Links aren't set up in src/account/AccountContext.js, so no payment is taken."
          : `Checkout opens on Stripe. Renewing adds a year to your end date, so renewing early loses nothing.${session.plan !== "all" && STRIPE.links.upgrade ? " Upgrading keeps your end date." : ""} You'll get a new passcode to save.`}
      </span>
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

// Population served decides the ADA Title II compliance date (DOJ, April 2026):
// April 26, 2027 for 50,000+ people; April 26, 2028 for smaller and special districts.

/* ============================================================
   Back from Stripe
   ============================================================ */

// Stripe sends the browser back here after paying: /checkout?session_id=cs_...
// If the return checks out, the passcode for what was bought is issued and
// shown (onPasscode). Otherwise it says why and how to get help.
export function CheckoutReturn({ onPasscode }) {
  const { applyPurchase, session } = useAuth();
  const { navigate } = useNavigation();
  const [state, setState] = useState({ status: "checking" });
  useOnce(() => { // React StrictMode runs effects twice in development: issue exactly once
    (async () => {
      const back = readCheckoutReturn();
      if (!back.ok) { setState({ status: "failed", ...back }); return; }
      try {
        const code = await applyPurchase(back.pending);
        finishCheckout(back.sessionId);
        onPasscode(code, { ...back.pending, checkoutId: back.sessionId });
      } catch (err) {
        setState({ status: "failed", sessionId: back.sessionId, reason: err.message });
      }
    })();
  });

  const support = LEGAL.contactEmail
    ? mailto(LEGAL.contactEmail, "Passcode after payment", `Stripe checkout: ${state.sessionId || "(see my receipt email)"}\nWorkspace name: \n`)
    : null;

  return (
    <GateCard>
        <SectionHeading title="Finishing your purchase" subtitle="Payment is handled by Stripe. Your passcode is made here, in your browser." />
        {state.status === "checking" && <StatusRow tone="working">Checking your return from Stripe…</StatusRow>}
        {state.status === "failed" && (
          <>
            <StatusRow tone="error" role="alert">
              {state.reason}{" "}
              {state.used ? "Sign in with the passcode you saved." : "If you paid, your Stripe receipt email is your proof: contact us with it and we'll send your passcode. Please don't pay again."}
            </StatusRow>
            {state.sessionId && <p className="contrast-note">Stripe checkout ID: <code>{state.sessionId}</code></p>}
            <div className="aside-panel__actions">
              {support && <a className="btn-pill btn-pill--outline" href={support}>Email support</a>}
              <button type="button" className="btn-pill btn-pill--outline" onClick={() => navigate(session ? "settings" : "login", { mode: "login" })}>
                {session ? "Back to Settings" : "Log in"}
              </button>
            </div>
          </>
        )}
    </GateCard>
  );
}

/* ============================================================
   Pricing
   ============================================================ */

// Public pricing page: the two plans, each leading to the "Get started" form
// with that plan chosen. Signed-in people see their plan, renewal and
// upgrade in Settings instead.
export function PricingPanel({ onChoose }) {
  return (
    <React.Fragment>
      <SectionHeading title="Pricing" subtitle="Every feature in both plans. Pick how many departments will use it." />
      <div className="pricing">
        {PLANS.map((plan) => (
          <section key={plan.id} className={"pricing__plan" + (plan.id === "all" ? " pricing__plan--featured" : "")}
                   aria-labelledby={`plan-${plan.id}`}>
            {plan.id === "all" && <p className="pricing__badge">Best for a whole government</p>}
            <h3 className="pricing__name" id={`plan-${plan.id}`}>{plan.name}</h3>
            <p className="pricing__price"><strong>{plan.price}</strong> <span>{plan.period}</span></p>
            <p className="pricing__blurb">{plan.blurb}</p>
            <ul className="pricing__features">
              {plan.features.map((f) => <li key={f}><CheckIcon size={14} strokeWidth={2.5} /> <span>{f}</span></li>)}
            </ul>
            <div className="pricing__action">
              <button type="button" className="aside-panel__save u-btn-solid pricing__cta" onClick={() => onChoose(plan.id)}
                      aria-describedby={`plan-${plan.id}`}>
                Choose {plan.name}
              </button>
            </div>
          </section>
        ))}
      </div>
      <dl className="pricing__faq">
        <dt>How do I pay?</dt>
        <dd>By card on Stripe's secure checkout. This site never sees your card. You get a receipt by email from Stripe.</dd>
        <dt>What do I get?</dt>
        <dd>A passcode, shown as soon as you're back from checkout. With your workspace name, it signs in on any device for a year.</dd>
        <dt>What happens after a year?</dt>
        <dd>Renew in Settings. Renewing early adds a full year to your end date, so you never lose days. Your designs stay on your devices either way.</dd>
        <dt>Can we start with one department?</dt>
        <dd>Yes. Upgrade to all departments any time in Settings.</dd>
        <dt>Need an invoice, purchase order or W-9?</dt>
        <dd>Email {LEGAL.contactEmail ? <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> : "us"} before buying and we'll set it up. Both plans are under $500 a year, which is within many purchasing-card limits.</dd>
      </dl>
    </React.Fragment>
  );
}
